/**
 * RoleSlotCard — Individual role slot display
 *
 * Shows: label, category badge, status pill, description, fill progress,
 * skill tags, time commitment, assigned user avatars, and action buttons.
 *
 * Used inside ProjectTeamPanel for each role slot on a project.
 */

import React, { useMemo, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import type {
  ProjectRoleSlotResponse,
  ProjectRoleAssignmentResponse,
} from "../../api/projectRoles";
import type { SecurityUserResponse } from "../../api/security";
import { mediaSrc } from "../../api/mediaUrl";
import styles from "./RoleSlotCard.module.css";

interface Props {
  slot: ProjectRoleSlotResponse;
  /** Map of userGuid → SecurityUserResponse for avatar/name display. */
  userMap: Map<string, SecurityUserResponse>;
  /** Project owner GUID — used for owner-default badge. */
  ownerGuid: string;
  canEdit: boolean;
  /**
   * DEV-1044. The operational bundle name currently granted to this slot.
   * null when no active grant exists (legacy slots pre-DEV-1042). Rendered
   * in the header next to the category badge to surface "what authority
   * does this role carry" at a glance.
   */
  bundleName?: string | null;
  onEdit?: (slot: ProjectRoleSlotResponse) => void;
  onAssign?: (slot: ProjectRoleSlotResponse) => void;
  onClose?: (slot: ProjectRoleSlotResponse) => void;
  onToggleVisibility?: (slot: ProjectRoleSlotResponse) => void;
  onViewAssignment?: (assignment: ProjectRoleAssignmentResponse, slot: ProjectRoleSlotResponse) => void;
  /** Drag-and-drop reordering support */
  isDragging?: boolean;
  isDragOver?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2)
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (name.slice(0, 2) || "??").toUpperCase();
}

function profileImgUrl(user: SecurityUserResponse): string | null {
  return mediaSrc(user.profileImageUrl);
}

const STATUS_CLASS: Record<string, string> = {
  draft: styles.statusDraft,
  open: styles.statusOpen,
  partially_filled: styles.statusPartial,
  filled: styles.statusFilled,
  closed: styles.statusClosed,
};

const RoleSlotCard: React.FC<Props> = ({
  slot,
  userMap,
  ownerGuid,
  canEdit,
  bundleName,
  onEdit,
  onAssign,
  onClose,
  onToggleVisibility,
  onViewAssignment,
  isDragging,
  isDragOver,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
}) => {
  const { t } = useAppTranslation("projectTeam");

  const activeAssignments = useMemo(
    () =>
      (slot.assignments || []).filter((a) => a.status === "active"),
    [slot.assignments]
  );

  const invitedAssignments = useMemo(
    () =>
      (slot.assignments || []).filter((a) => a.status === "pending_invitation"),
    [slot.assignments]
  );

  const pendingCount = useMemo(
    () =>
      (slot.assignments || []).filter((a) => a.status === "pending_approval").length,
    [slot.assignments]
  );

  const HISTORICAL_STATUSES = new Set(["completed", "resigned", "removed", "expired"]);

  const historicalAssignments = useMemo(
    () =>
      (slot.assignments || []).filter((a) => HISTORICAL_STATUSES.has(a.status)),
    [slot.assignments]
  );

  const [showHistory, setShowHistory] = useState(false);

  const fillPct = slot.slotsNeeded > 0
    ? Math.min(100, Math.round((slot.slotsFilled / slot.slotsNeeded) * 100))
    : 0;

  const isOpen = slot.status === "open" || slot.status === "partially_filled";
  const isClosed = slot.status === "closed";

  const wrapperClasses = [
    styles.cardWrapper,
    isDragging ? styles.dragging : "",
    isDragOver ? styles.dragOver : "",
  ].filter(Boolean).join(" ");

  return (
    <div
      className={wrapperClasses}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* Drag handle */}
      <div
        className={canEdit ? styles.dragHandle : styles.dragHandleHidden}
        draggable={canEdit}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        title="Drag to reorder"
      >
        <div className={styles.dragHandleIcon}>
          <div className={styles.dragHandleRow}>
            <div className={styles.dragHandleDot} />
            <div className={styles.dragHandleDot} />
          </div>
          <div className={styles.dragHandleRow}>
            <div className={styles.dragHandleDot} />
            <div className={styles.dragHandleDot} />
          </div>
          <div className={styles.dragHandleRow}>
            <div className={styles.dragHandleDot} />
            <div className={styles.dragHandleDot} />
          </div>
        </div>
      </div>

      <div className={`${styles.card} ${canEdit ? styles.cardWithHandle : ""}`}>
      {/* Header */}
      <div className={styles.header}>
        <span className={styles.label}>{slot.roleLabel}</span>
        <span className={styles.categoryBadge}>
          {t(`categories.${slot.roleCategory}`)}
        </span>
        {/* DEV-1044. Bundle badge — surfaces operational authority of the
            role. Distinct color from the category badge so the two read as
            "what kind of role" + "what powers it carries". */}
        {bundleName ? (
          <span className={styles.bundleBadge} title={bundleName}>
            {bundleName}
          </span>
        ) : (
          <span className={styles.bundleBadgeNone} title={t("card.noBundleHelp")}>
            {t("card.noBundle")}
          </span>
        )}
        <span
          className={`${styles.statusPill} ${STATUS_CLASS[slot.status] || ""}`}
        >
          {t(`statuses.${slot.status}`)}
        </span>
        {pendingCount > 0 && (
          <span className={styles.pendingBadge}>
            {pendingCount} {t("card.pendingApprovals")}
          </span>
        )}
      </div>

      {/* Description */}
      {slot.description && (
        <div className={styles.description}>{slot.description}</div>
      )}

      {/* Meta row */}
      <div className={styles.metaRow}>
        {/* Fill progress */}
        <span className={styles.fillProgress}>
          {slot.slotsFilled}/{slot.slotsNeeded}
          <span className={styles.fillBar}>
            <span
              className={styles.fillBarInner}
              style={{ width: `${fillPct}%` }}
            />
          </span>
        </span>

        {/* Time commitment */}
        {slot.timeCommitment && (
          <span className={styles.timeCommitment}>
            {slot.timeCommitment}
          </span>
        )}

        {/* Skills (first 3) */}
        {slot.skillsRequired.slice(0, 3).map((skill) => (
          <span key={skill} className={styles.skillTag}>
            {skill}
          </span>
        ))}
        {slot.skillsRequired.length > 3 && (
          <span className={styles.skillTag}>
            +{slot.skillsRequired.length - 3}
          </span>
        )}
      </div>

      {/* Assigned users */}
      {(activeAssignments.length > 0 || invitedAssignments.length > 0 || slot.isOwnerDefault) && (
        <div className={styles.avatarRow}>
          {/* Owner-default placeholder */}
          {slot.isOwnerDefault && activeAssignments.length === 0 && invitedAssignments.length === 0 && (
            <>
              {renderAvatar(
                userMap.get(ownerGuid.toUpperCase())
              )}
              <span className={styles.ownerBadge}>
                {t("card.ownerDefault")}
              </span>
            </>
          )}

          {/* Active assignments */}
          {activeAssignments.map((a) => {
            const user = userMap.get(a.userGuid.toUpperCase());
            return (
              <div
                key={a.assignmentGuid}
                style={{ cursor: onViewAssignment ? 'pointer' : 'default' }}
                onClick={() => onViewAssignment?.(a, slot)}
                role={onViewAssignment ? 'button' : undefined}
                tabIndex={onViewAssignment ? 0 : undefined}
                onKeyDown={(e) => e.key === 'Enter' && onViewAssignment?.(a, slot)}
              >
                {renderAvatar(user)}
              </div>
            );
          })}

          {/* Invited (pending) assignments — shown with opacity */}
          {invitedAssignments.map((a) => {
            const user = userMap.get(a.userGuid.toUpperCase());
            return (
              <React.Fragment key={a.assignmentGuid}>
                <div className={styles.avatarInvited} title={t("card.invited")}>
                  {renderAvatar(user)}
                </div>
              </React.Fragment>
            );
          })}

          {/* Remaining slots indicator */}
          {isOpen && slot.slotsFilled < slot.slotsNeeded && (
            <span className={styles.emptySlots}>
              {t("card.slotsRemaining", {
                count: slot.slotsNeeded - slot.slotsFilled,
              })}
            </span>
          )}
        </div>
      )}

      {/* Historical assignments (completed/resigned/removed/expired) */}
      {historicalAssignments.length > 0 && (
        <div className={styles.historySection}>
          <button
            type="button"
            className={styles.historyToggle}
            onClick={() => setShowHistory((v) => !v)}
          >
            <span className={styles.historyToggleIcon}>
              {showHistory ? "▾" : "▸"}
            </span>
            {t("card.pastMembers", { count: historicalAssignments.length })}
          </button>
          {showHistory && (
            <div className={styles.historyList}>
              {historicalAssignments.map((a) => {
                const user = userMap.get(a.userGuid.toUpperCase());
                const statusKey =
                  a.status === "completed" ? "completed" :
                  a.status === "resigned" ? "resigned" :
                  a.status === "removed" ? "removed" :
                  "expired";
                return (
                  <div
                    key={a.assignmentGuid}
                    className={styles.historyRow}
                    style={{ cursor: onViewAssignment ? "pointer" : "default" }}
                    onClick={() => onViewAssignment?.(a, slot)}
                    role={onViewAssignment ? "button" : undefined}
                    tabIndex={onViewAssignment ? 0 : undefined}
                    onKeyDown={(e) => e.key === "Enter" && onViewAssignment?.(a, slot)}
                  >
                    <div className={styles.historyAvatar}>
                      {renderAvatar(user)}
                    </div>
                    <span className={styles.historyName}>
                      {user?.displayName || user?.name || "??"}
                    </span>
                    <span className={`${styles.historyStatus} ${styles[`historyStatus_${statusKey}`] || ""}`}>
                      {t(`card.historyStatus.${statusKey}`)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      {canEdit && !isClosed && (
        <div className={styles.actions}>
          {/* Visibility toggle — far left */}
          {onToggleVisibility && (
            <button
              type="button"
              className={
                slot.visibility === "public"
                  ? styles.actionBtnAdvertise
                  : styles.actionBtnInternal
              }
              onClick={() => onToggleVisibility(slot)}
            >
              {slot.visibility === "public"
                ? t("card.advertised")
                : t("card.internal")}
            </button>
          )}
          <div className={styles.actionsSpacer} />
          {onEdit && (
            <button
              type="button"
              className={styles.actionBtn}
              onClick={() => onEdit(slot)}
            >
              {t("card.edit")}
            </button>
          )}
          {onAssign && (
            <button
              type="button"
              className={styles.actionBtnPrimary}
              onClick={() => onAssign(slot)}
            >
              {isOpen ? t("card.assign") : t("card.manageTeam")}
            </button>
          )}
          {onClose && slot.status !== "draft" && (
            <button
              type="button"
              className={styles.actionBtn}
              onClick={() => onClose(slot)}
            >
              {t("card.close")}
            </button>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

function renderAvatar(user: SecurityUserResponse | undefined) {
  if (!user) {
    return (
      <div className={styles.avatar}>
        <span className={styles.avatarInitials}>??</span>
      </div>
    );
  }
  const img = profileImgUrl(user);
  return (
    <div className={styles.avatar}>
      {img ? (
        <img src={img} alt="" className={styles.avatarImg} />
      ) : (
        <span className={styles.avatarInitials}>
          {getInitials(user.displayName || user.name)}
        </span>
      )}
    </div>
  );
}

export default RoleSlotCard;
